import { useEffect, useMemo, useState, useRef } from "react";
import "./ReferenceBubbles.css";

function ReferenceBubbles({ title, subText, references }) {
  const [highlightIndex, setHighlightIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollRef = useRef();

  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 600px)").matches;

  const shuffledReferences = useMemo(
    () => [...references].sort(() => Math.random() - 0.5),
    [references]
  );

  const refsPerSlide = 7;
  const totalSlides = isMobile
    ? Math.ceil(shuffledReferences.length / refsPerSlide)
    : 0;

  const slideChunks = useMemo(() => {
    if (!isMobile) return [];
    const chunks = [];
    for (let i = 0; i < shuffledReferences.length; i += refsPerSlide) {
      chunks.push(shuffledReferences.slice(i, i + refsPerSlide));
    }
    return chunks;
  }, [isMobile, shuffledReferences, refsPerSlide]);

  useEffect(() => {
    let length;
    if (isMobile) {
      const currentSlideItems = slideChunks[activeSlide] ?? [];
      length = currentSlideItems.length;
    } else {
      length = shuffledReferences.length;
    }

    if (!length) return;

    const interval = setInterval(() => {
      setHighlightIndex((prev) => {
        let next;
        do {
          next = Math.floor(Math.random() * length);
        } while (length > 1 && next === prev);
        return next;
      });
    }, isMobile ? 3800 : 2000);

    return () => clearInterval(interval);
  }, [isMobile, slideChunks, activeSlide, shuffledReferences.length]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    setActiveSlide(index);
    setHighlightIndex(null);
  };

  return (
    <section className="references-container">
      <div className="references-inner">
        <div className="references-header">
          <h2>{title}</h2>
          <p>{subText}</p>
        </div>

        <div
          className="references-field hover-lift"
          ref={scrollRef}
          onScroll={isMobile ? handleScroll : undefined}
        >
          {isMobile ? (
            <div className="reference-slider">
              {slideChunks.map((slide, sIdx) => (
                <div key={sIdx} className="references-slide">
                  {slide.map((text, idx) => (
                    <span
                      key={idx}
                      className={
                        "reference-bubble" +
                        (sIdx === activeSlide && idx === highlightIndex
                          ? " reference-bubble--highlight"
                          : "")
                      }
                    >
                      {text}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="references-field-bg" />
              <div className="references-bubbles">
                {shuffledReferences.map((text, idx) => (
                  <span
                    key={idx}
                    className={
                      "reference-bubble" +
                      (idx === highlightIndex
                        ? " reference-bubble--highlight"
                        : "")
                    }
                  >
                    {text}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {isMobile && totalSlides > 1 && (
          <div className="refs-pagination">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <span
                key={i}
                className={
                  "refs-dot" + (i === activeSlide ? " refs-dot--active" : "")
                }
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ReferenceBubbles;
